// The alert-cluster card (docs/alert-clusters-refactor.md) — the one insight.
//
// Replaces InsightTile/InsightDrawer/InsightDetail and the four bespoke
// detail cards. Every value it shows was computed server-side; nothing here
// is model-written, so the card has no vet verdict, causality tier or
// degraded-source state to represent (ruling 13).
//
// All of the card's words live in clusterCard.ts, which is React-free and
// exhaustive over the nine (state x rung) pairs. This file is layout, and it
// follows the 2026-09-14 design: banded sections in their own panels, a
// gridded chart with a callout on the affected window, tinted finding tiles,
// and the absences demoted to a row of checks at the foot.

import { useEffect, useId, useLayoutEffect, useState } from 'react'
import type {
  CSSProperties, FocusEvent, KeyboardEvent, PointerEvent, ReactNode,
} from 'react'
import { useRef } from 'react'

import {
  AllClearIcon, BellIcon, CauseIcon, CommentIcon, GroupIcon, InfoIcon,
  PartlyIcon, RepeatIcon, ScaleArrow, SparkMark, StillOpenIcon, TagIcon,
  ThumbDownIcon, ThumbUpIcon,
} from './clusterIcons'
import { SEVERITY } from './severity'
import {
  type AlertClusterRow,
  type ClusterFact,
  type FactKey,
  type MatchDay,
  type MatchHistory,
  BAND_CAP_LEAD,
  CARD_TITLE,
  CHART_HINT,
  CHART_TITLE,
  CHART_WINDOW,
  CHECKS_LABEL,
  FACTS_HINT,
  FACTS_LABEL,
  FEEDBACK_NOTE,
  FEEDBACK_TITLE,
  NO_OPEN_NOTE,
  SCALE_CONTEXT,
  SHOW_FEWER,
  SLOT_LABEL,
  SPARK_CTA,
  SPARK_FLOOR_PCT,
  SPARK_HOW,
  SPARK_NO_BAND,
  SPARK_REST,
  SPARK_REST_NOTE,
  SPARK_TITLE,
  TRUNCATED_NOTE,
  activeLine,
  bandLabel,
  bandSpan,
  bandStats,
  barLabel,
  barReadout,
  bars,
  blocks,
  clockAt,
  columnAnchor,
  dayReadout,
  contextRun,
  markerRun,
  facts,
  factsNote,
  fleetNote,
  formatWindow,
  gaps,
  hourLabel,
  moreLabel,
  unlistedNote,
  openClause,
  presentLevels,
  reachParts,
  spanShort,
  spanWords,
  sparkDescription,
  sparkLabel,
  sparkReadout,
  sparkScale,
  sparkToneNote,
  sparkWindow,
  stateChip,
  tileChip,
  tileHeadline,
  tileSub,
  tileTone,
  viewEventsHint,
  viewEventsLabel,
  viewOpenLabel,
  yTicks,
  chartPeak,
  usualBand,
  volumeLine,
  volumeShort,
} from './clusterCard'

export type { AlertClusterRow } from './clusterCard'

const sevLabel = (level: number) => SEVERITY[level as 0 | 1 | 2 | 3 | 4 | 5]?.label ?? `Level ${level}`
const sevColor = (level: number) => SEVERITY[level as 0 | 1 | 2 | 3 | 4 | 5]?.color ?? 'var(--faint)'

function ChevIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * A hint behind an (i), for the sentences a heading has no room for.
 *
 * Hover AND focus AND click, all three: hover alone is the `title` attribute's
 * failure repeated (no touch, no keyboard), and click alone hides the hint
 * behind a gesture nobody makes on a glyph this size. It is a button, so it
 * is in the tab order and announced; `aria-describedby` hangs the text off it
 * rather than leaving a screen reader with an unexplained icon.
 *
 * Never load-bearing. Everything behind an (i) on this card is a rule that
 * QUALIFIES a claim the reader can already see in full — a reader who never
 * opens one is not misled, only less informed.
 */
function Hint({ text, label, align = 'start' }: {
  text: string
  label: string
  align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <span className="nw-ac-hint">
      <button
        type="button"
        className="nw-ac-hint-b"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
      >
        <InfoIcon />
      </button>
      {open && (
        <span className={`nw-ac-hint-p a-${align}`} role="tooltip" id={id}>{text}</span>
      )}
    </span>
  )
}

const FACT_ICON: Record<FactKey, ReactNode> = {
  group: <GroupIcon />,
  dominant: <BellIcon />,
  tags: <TagIcon />,
  cause: <CauseIcon />,
  matches: <RepeatIcon />,
}

/**
 * The findings, one tinted tile each.
 *
 * Every tile here is an ANSWER. A question that came back empty is a row in
 * the checks band below, which is what makes the tones affordable: the
 * previous chain spent four tiles of colour on a set in which two usually
 * held an absence, and a pastel field whose emptiest boxes are as loud as its
 * findings scans as findings.
 *
 * Two to a row on the wide card, one on a phone. The tile's own parts run
 * label / answer / fraction / footnote — value first among the things a
 * reader is scanning for, with the arithmetic under it and the sentence that
 * explains the arithmetic under that.
 */
function Facts({ row, rows }: { row: AlertClusterRow; rows: ClusterFact[] }) {
  return (
    <section className="nw-ac-sec">
      <h3 className="nw-ac-label">
        {FACTS_LABEL}
        <Hint text={FACTS_HINT} label="What these findings are counted from" />
      </h3>
      <p className="nw-ac-sub">{factsNote(row)}</p>
      <div className="nw-ac-tiles">
        {rows.map((f) => <FactTile f={f} key={f.key} />)}
      </div>
    </section>
  )
}

function FactTile({ f }: { f: ClusterFact }) {
  // Only the history tile opens. Open, it takes the whole row: the list then
  // flows into columns rather than stretching the tile beside it to the
  // height of eighteen dates, and it never becomes a scroll box inside the
  // dialog's own scroll.
  const [open, setOpen] = useState(false)
  return (
    <div className={`nw-ac-tile t-${f.tone} f-${f.key}${open ? ' is-open' : ''}`}>
      <span className="nw-ac-tile-i" aria-hidden="true">{FACT_ICON[f.key]}</span>
      <span className="nw-ac-tile-b">
        <span className="nw-ac-tile-l">{f.label}</span>
        <span className={`nw-ac-tile-v${f.mono ? ' is-mono' : ''}`}>{f.value}</span>
        {f.stat && (
          <span className="nw-ac-tile-st">
            {f.stat.map((p, i) => (
              p.strong ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>
            ))}
          </span>
        )}
        {f.sub?.map((sline) => (
          <span className="nw-ac-tile-s" key={sline}>{sline}</span>
        ))}
        {f.history && (
          <HistoryBody h={f.history} open={open} onToggle={() => setOpen((o) => !o)} />
        )}
        {f.note && <span className="nw-ac-tile-n">{f.note}</span>}
      </span>
    </div>
  )
}

/**
 * Which of the last 30 days a pattern turned up on — the question a list of
 * dates stops answering somewhere past three.
 *
 * A quiet day keeps a tick on the baseline, as a quiet hour does on the chart
 * above: drawn as a gap it would read as a day nobody looked. Neutral ink,
 * never the semaphore — this says how often, not how bad.
 *
 * Hidden from assistive tech. The stat line above it states what it shows
 * ("On 12 of the last 30 days"), and the list below dates every occurrence;
 * the per-day readout on hover is a convenience for a pointer and nothing a
 * reader without one is missing.
 */
function MatchStrip({ days }: { days: MatchDay[] }) {
  const [cur, setCur] = useState<number | null>(null)
  const strip = { '--nw-ac-days': days.length } as CSSProperties
  return (
    <span className="nw-ac-days" aria-hidden="true">
      <span className="nw-ac-days-bar" style={strip} onMouseLeave={() => setCur(null)}>
        {days.map((d, i) => (
          <i
            key={d.at}
            className={[
              d.count === 0 ? 'q' : d.count > 1 ? 'm' : '',
              cur === i ? 'is-cur' : '',
            ].filter(Boolean).join(' ') || undefined}
            onMouseEnter={() => setCur(i)}
          />
        ))}
      </span>
      <span className="nw-ac-days-ends">
        <span>{days[0].label}</span>
        <b>{cur === null ? '' : dayReadout(days[cur])}</b>
        <span>{days[days.length - 1].label}</span>
      </span>
    </span>
  )
}

function HistoryBody({ h, open, onToggle }: {
  h: MatchHistory
  open: boolean
  onToggle: () => void
}) {
  const listId = useId()
  const more = h.rows.length > h.recent
  return (
    <>
      {h.days && <MatchStrip days={h.days} />}
      <ol className={`nw-ac-hist${open ? ' is-open' : ''}`} id={listId}>
        {h.rows.map((r, i) => (
          <li key={i} hidden={!open && i >= h.recent}>{r}</li>
        ))}
      </ol>
      {open && h.unlisted > 0 && (
        <span className="nw-ac-tile-s">{unlistedNote(h)}</span>
      )}
      {more && (
        <button
          type="button"
          className="nw-ac-hist-more"
          aria-expanded={open}
          aria-controls={listId}
          onClick={onToggle}
        >
          {open ? SHOW_FEWER : moreLabel(h)}
          <ChevIcon />
        </button>
      )}
    </>
  )
}

/**
 * What was looked for and not found.
 *
 * Each check states its RULE, which is the part that must survive: "no recent
 * configuration change" alone reads as a verdict on the environment, and
 * naming the 45-minute window hands the reader what they need to judge it.
 * Where the rule is too long for the row it sits behind the (i) instead, and
 * the row carries a brief.
 *
 * THE TICK IS THE DESIGN'S, and it is worth naming what it costs: a green
 * check is the vocabulary of a test that PASSED, and not finding a recorded
 * change is not the same as there having been none. The titles carry the
 * whole load of keeping that honest — each says what was not found, never
 * that anything is fine.
 */
function Checks({ rows }: { rows: ReturnType<typeof gaps> }) {
  return (
    <section className="nw-ac-sec">
      <h3 className="nw-ac-label">{CHECKS_LABEL}</h3>
      <div className="nw-ac-checks">
        {rows.map((g) => (
          <div className="nw-ac-check" key={g.key}>
            <span className="nw-ac-check-i" aria-hidden="true"><AllClearIcon /></span>
            <span className="nw-ac-check-b">
              <span className="nw-ac-check-t">
                {g.title}
                {g.hint && <Hint text={g.hint} label="How this was checked" align="end" />}
              </span>
              <span className="nw-ac-check-n">{g.note}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * What one bar says when a reader asks it.
 *
 * Replaces the browser's `title` attribute, which is a tooltip belonging to
 * no design system: OS font and colours, a second's delay, unstyleable,
 * absent on touch entirely, and unreachable from the keyboard. It also could
 * not hold the thing most worth showing — the per-severity split the stacked
 * bar encodes but which cannot be read off it precisely.
 *
 * Positioned with the same grid-column trick as the band and hung off the
 * same `edgeAnchor` rule, so a readout at 23:00 leans back into the chart
 * instead of off it. The wrapper takes `pointer-events: none`, so the box can
 * never sit between the pointer and the column it describes.
 */
function BarReadoutBox({ bar, row, anchor, column, id }: {
  bar: ReturnType<typeof bars>[number]
  row: AlertClusterRow
  anchor: 'start' | 'mid' | 'end'
  /** 1-based grid column — the hovered hour. Placed EXPLICITLY for the same
   *  reason every bar is: a grid auto-places an unplaced item after the
   *  explicit ones, which parked the readout at the right-hand edge of the
   *  chart whatever column it was describing. */
  column: number
  id: string
}) {
  const r = barReadout(bar, row)
  return (
    <div
      className={`nw-ac-tip a-${anchor}`}
      style={{ gridColumn: column }}
      id={id}
      role="tooltip"
    >
      <div className="nw-ac-tip-b">
        <span className="nw-ac-tip-w">{r.window}</span>
        <span className="nw-ac-tip-n">{r.total}</span>
        {r.levels.length > 0 && (
          <span className="nw-ac-tip-rows">
            {r.levels.map((l) => (
              <span className="nw-ac-tip-r" key={l.level}>
                <i style={{ background: sevColor(l.level) }} aria-hidden="true" />
                <span className="k">{sevLabel(l.level)}</span>
                <span className="v">{l.count}</span>
              </span>
            ))}
          </span>
        )}
        {r.usual && <span className="nw-ac-tip-u">{r.usual}</span>}
        {r.inCluster && <span className="nw-ac-tip-in">{BAND_CAP_LEAD}</span>}
      </div>
    </div>
  )
}

/**
 * The 24h stacked histogram, with the cluster's own hours banded and called
 * out.
 *
 * THE BAND is ONE region drawn across the cluster's columns, behind the bars.
 * It used to be a per-column wash, which read as a PHANTOM BAR whenever a
 * banded hour was quiet — a block the height of the chart with no data in it.
 * A single region cannot be misread that way: it spans several columns at
 * once, it is plainly behind the bars rather than among them, and it now
 * carries a three-line callout above it saying what it is, when it was and
 * what landed in it.
 *
 * Accent, not red. The bars already spend the whole warm range on severity;
 * a red region over red bars would be mud, and would state a second opinion
 * about badness that this band does not hold.
 *
 * THE Y AXIS is the design's addition and the chart's most useful one. Bars
 * scaled to their own tallest member make every chart look equally busy —
 * a quiet night peaking at three drew the same silhouette as a storm peaking
 * at ninety. A rounded ceiling with labelled gridlines gives the height a
 * unit, which is what lets a reader say the banded hours were twice the
 * day's normal rather than merely taller than it.
 *
 * Callout, plot, axis and scale strip are four grids on ONE column template,
 * so every annotation lands on exact column edges with no percentage
 * arithmetic to drift.
 */
function Histogram({ row }: { row: AlertClusterRow }) {
  const data = bars(row)
  // The column the readout describes: hovered, or focused. ONE piece of state
  // for both, so a pointer and a keyboard can never light up two columns.
  const [cursor, setCursor] = useState<number | null>(null)
  // Roving tabindex: exactly one column sits in the tab order and the arrows
  // move between them. Twenty-four tab stops inside a dialog is a worse
  // answer than one, and a chart nobody can reach without a mouse is what the
  // `title` attribute already was.
  const [stop, setStop] = useState(0)
  const plotRef = useRef<HTMLDivElement>(null)
  const tipId = useId()
  if (data.length === 0) return null
  // Each hour's usual range, when the server had the history to say. It
  // raises the ceiling too: a quiet day's usual range drawn off the top of
  // the chart would read as "nothing is usual here".
  const usual = usualBand(row, data)
  const peak = chartPeak(data, usual)
  // Bars are drawn against the ROUNDED ceiling, never the peak, so a bar's
  // height and the gridline beside it state the same number.
  const { max, ticks } = yTicks(peak)
  const levels = presentLevels(row)
  const band = bandSpan(data)
  // The axis labels only every Nth bar: 24 timestamps at this width overlap
  // into an unreadable smear.
  const labelEvery = 3
  const grid = { '--nw-ac-n': data.length } as CSSProperties
  const bandCols = band
    ? { gridColumn: `${band.start + 1} / ${band.end + 2}` }
    : undefined
  // The strip is a ruler: the bar is the whole day, the slice is the window
  // at its own columns, and the label goes in whichever run is left over.
  const ctxRun = contextRun(band, data.length)
  const winShort = row.state === 'live'
    ? null : spanShort(row.last_alert_at - row.first_alert_at)
  // The duration goes IN the strip when a run beside the window can hold it,
  // and falls back to the caption when none can.
  const mkRun = winShort ? markerRun(band, data.length) : null

  const move = (to: number) => {
    const next = Math.min(data.length - 1, Math.max(0, to))
    setStop(next)
    setCursor(next)
    plotRef.current?.querySelector<HTMLElement>(`[data-col="${next}"]`)?.focus()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { setCursor(null); return }
    const to = e.key === 'ArrowRight' ? stop + 1
      : e.key === 'ArrowLeft' ? stop - 1
        : e.key === 'Home' ? 0
          : e.key === 'End' ? data.length - 1
            : null
    if (to === null) return
    e.preventDefault()
    move(to)
  }

  return (
    <section className="nw-ac-chart">
      <div className="nw-ac-chart-h">
        <div className="nw-ac-chart-hb">
          <h3 className="nw-ac-chart-t">
            {CHART_TITLE}
            <Hint text={CHART_HINT} label="What this chart counts" />
          </h3>
          <span className="nw-ac-chart-w">{CHART_WINDOW}</span>
        </div>
        <span className="nw-ac-legend">
          {usual && (
            <span className="nw-ac-legend-i is-usual">
              <i aria-hidden="true" />
              Usual range
            </span>
          )}
          {levels.map((l) => (
            <span className="nw-ac-legend-i" key={l}>
              <i style={{ background: sevColor(l) }} aria-hidden="true" />
              {sevLabel(l)}
            </span>
          ))}
        </span>
      </div>

      <div className="nw-ac-body" style={grid}>
        {/* Its own row above the plot, so the callout can be three lines tall
            without eating the headroom the bars are drawn in. */}
        {band && (
          <div className="nw-ac-callrow" aria-hidden="true">
            <div className={`nw-ac-call a-${band.anchor}`} style={bandCols}>
              <span className="nw-ac-call-b">
                <span className="l">{BAND_CAP_LEAD}</span>
                <span className="w">
                  {row.state === 'live'
                    ? `since ${clockAt(row.first_alert_at, row.tz)}`
                    : formatWindow(row.first_alert_at, row.last_alert_at, row.tz)}
                </span>
                <span className="s">{bandStats(row)}</span>
              </span>
            </div>
          </div>
        )}

        {/* A group, not role="img". An image's subtree is hidden from assistive
            tech, which was right while the only way to read a bar was a `title`
            no screen reader announces — but it would now hide the very labels
            the readout put there. */}
        <div
          className="nw-ac-plot"
          ref={plotRef}
          role="group"
          aria-label={`Alerts per hour over the last 24 hours, up to ${max} an hour. ${bandLabel(row)}. Use the arrow keys to read an hour.`}
          onKeyDown={onKeyDown}
          onMouseLeave={() => setCursor(null)}
          // Only when focus leaves the plot entirely: moving between columns
          // fires a blur before the next column's focus, and clearing on every
          // blur made the readout flicker off on each arrow press.
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setCursor(null)
          }}
        >
          {/* One element spanning the whole plot, with a line per tick hung
              off its bottom edge — so the lines and the bars measure from the
              same baseline by construction. */}
          <div className="nw-ac-ticks" aria-hidden="true">
            {ticks.map((v) => (
              <span
                className="nw-ac-tickline"
                key={v}
                style={{ bottom: `${(v / max) * 100}%` }}
              >
                <i>{v}</i>
              </span>
            ))}
          </div>
          {cursor !== null && data[cursor] && (
            <BarReadoutBox
              bar={data[cursor]}
              row={row}
              anchor={columnAnchor(cursor, data.length)}
              column={cursor + 1}
              id={tipId}
            />
          )}
          {band && <div className="nw-ac-band" style={bandCols} aria-hidden="true" />}
          {/* Each hour's usual range, behind the bar: the middle half of the
              same hour over the previous four weeks. Neutral, because the
              warm range belongs to severity and the accent to the band, and
              placed explicitly for the same reason every column is. */}
          {usual?.map((u, i) => u && (
            <span
              className="nw-ac-usual"
              key={`u${data[i].at}`}
              style={{ gridColumn: i + 1 }}
              aria-hidden="true"
            >
              <i
                style={{
                  bottom: `${(u.p25 / max) * 100}%`,
                  height: `${((u.p75 - u.p25) / max) * 100}%`,
                }}
              />
            </span>
          ))}
          {/* Every column is placed EXPLICITLY. The band is placed explicitly
              too, and a grid auto-places the rest AROUND an explicit item — so
              left to flow, every bar from the band rightwards slid one column
              over and the chart no longer agreed with its own axis. */}
          {data.map((b, i) => (
            <div
              // An hour with nothing in it keeps a tick on the baseline. Drawn
              // as a gap, a quiet night read as missing data rather than as a
              // measured zero — and this chart's whole job is to say how
              // unusual the banded hours were, which needs the quiet ones to
              // be legible as quiet.
              className={[
                'nw-ac-col',
                b.total === 0 ? 'is-quiet' : '',
                cursor === i ? 'is-cursor' : '',
              ].filter(Boolean).join(' ')}
              key={b.at}
              data-col={i}
              style={{ gridColumn: i + 1 }}
              // The column carries the same sentence the readout shows, built
              // from the same barReadout, so what is heard and what is seen
              // cannot drift into two descriptions of one bar.
              tabIndex={stop === i ? 0 : -1}
              aria-label={barLabel(b, row)}
              aria-describedby={cursor === i ? tipId : undefined}
              onMouseEnter={() => setCursor(i)}
              onFocus={() => { setStop(i); setCursor(i) }}
            >
              <span className="nw-ac-stack">
                {b.segments.map(([level, n]) => (
                  <i
                    key={level}
                    style={{
                      height: `${(n / max) * 100}%`,
                      background: sevColor(level),
                    }}
                  />
                ))}
              </span>
            </div>
          ))}
        </div>

        {/* Its own grid on the same template: the labels overflow their columns
            on purpose (a clock is wider than an hour at this scale) and would
            otherwise force the plot's columns apart. */}
        <div className="nw-ac-axis" aria-hidden="true">
          {data.map((b, i) => (
            <span key={b.at}>{i % labelEvery === 0 ? hourLabel(b.at, row.tz) : ''}</span>
          ))}
        </div>

        {/* The scale strip: a ruler for the chart above it. The bar is the
            whole day; the slice marks the affected window at ITS OWN columns,
            so a reader can see at a glance where in the day the cluster sits.
            The chart spends most of its width on hours that are NOT what the
            card is about, and without this a reader has only the band's tint
            to tell them so.

            It used to split the bar in two at the window's left edge and
            label the whole right-hand half with the window's duration — which
            claimed a 13-minute cluster at 09:27 was three hours wide, and put
            its caption right of where the cluster actually was. */}
        <div className="nw-ac-scale" aria-hidden="true">
          {/* The context label takes ONE arrow, on the outer end of its run —
              the bar's own end. Its inner end is the window, and the window
              brings its own mark there; two arrows meeting at one boundary
              would be the same statement made twice. With no window drawn
              yet the run is the whole bar and takes both. */}
          {ctxRun && (
            <span
              className={`nw-ac-scale-ctx o-${!band ? 'both' : ctxRun.start === 0 ? 'left' : 'right'}`}
              style={{ gridColumn: `${ctxRun.start + 1} / ${ctxRun.end + 1}` }}
            >
              <span className="a l"><ScaleArrow dir="left" /></span>
              <em>{SCALE_CONTEXT}</em>
              <span className="a r"><ScaleArrow dir="right" /></span>
            </span>
          )}
          {band && (
            /* The window, and the pair of arrows the design brackets it with,
               one from each side. They are drawn against its rules rather
               than laid out beside them, so they cost the bar no width and
               survive every width the card is rendered at — which the words
               below them do not. Dropped where there is no bar on that side
               to point across: a cluster in the day's first or last hour. */
            <span className="nw-ac-scale-win" style={bandCols}>
              {band.start > 0 && (
                <span className="nw-ac-win-a l"><ScaleArrow dir="right" /></span>
              )}
              {band.end < data.length - 1 && (
                <span className="nw-ac-win-a r"><ScaleArrow dir="left" /></span>
              )}
            </span>
          )}
          {mkRun && (
            /* The duration, just past the arrow on whichever side has room —
               the design's place for it, which is between the two arrows. */
            <span
              className={`nw-ac-scale-mark m-${mkRun.side}`}
              style={{
                gridColumn: `${mkRun.start + 1} / ${mkRun.end + 1}`,
                justifySelf: mkRun.side === 'right' ? 'start' : 'end',
              }}
            >
              <b>{winShort}</b>
            </span>
          )}
        </div>
        {band && (
          <div className="nw-ac-scale-cap" aria-hidden="true">
            {/* Hung off the same `edgeAnchor` rule as the callout and the
                readout: the caption is far wider than a one-hour slice, which
                is the common case, so near either end it leans back into the
                chart rather than off it. */}
            {/* The duration is printed twice and one copy is always hidden:
                the strip's own label needs a bar wide enough to hold it, and
                which bar that is, is a question only CSS can answer. */}
            <span className={`a-${band.anchor}`} style={bandCols}>
              {winShort && <b className={mkRun ? 'dup' : undefined}>{winShort}</b>}
              {winShort && (
                <i className={mkRun ? 'dup' : undefined} aria-hidden="true">·</i>
              )}
              {BAND_CAP_LEAD}
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

function Members({ row }: { row: AlertClusterRow }) {
  const rows = row.members ?? []
  if (rows.length === 0) return null
  return (
    <div className="nw-ac-members">
      {rows.map((m) => (
        <div className="nw-ac-member" key={m.eventid}>
          <span className="s" style={{ background: sevColor(m.severity) }}
                title={sevLabel(m.severity)} aria-label={sevLabel(m.severity)} />
          <span className="n">{m.name}</span>
          <span className="h">{m.host}</span>
          <span className="t">{clockAt(m.clock, row.tz)}</span>
          <span className={`r${m.recovered_at ? '' : ' open'}`}>
            {m.recovered_at ? `recovered ${clockAt(m.recovered_at, row.tz)}` : 'still open'}
          </span>
        </div>
      ))}
      {/* The real total, always — a list summing to the cap under a heading
          reading "58 alerts" is the two-counts bug the situation card shipped
          with. */}
      {row.members_total > rows.length && (
        <div className="nw-ac-member-more">
          Showing the {rows.length} most recent of {row.members_total}.
        </div>
      )}
    </div>
  )
}

/** How long the pointer rests on the sparkline before the card opens, so a
 *  pointer crossing the row on its way to the chevron does not flash it. */
const SPARK_OPEN_MS = 120
/** How long the card outlives the pointer leaving — the time it takes to
 *  cross the gap from the bars down onto the card. */
const SPARK_CLOSE_MS = 160
/** The sparkline's column gap, in px. Must match `.nw-act-spark`. */
const SPARK_GAP = 2

/**
 * Open, placement and the hour being read, for the card over the sparkline.
 *
 * The card opens from the pointer resting on the bars, and from keyboard
 * focus landing on the row — but only VISIBLE focus, so a click on the row
 * (which opens the dialog) never flashes it. It remembers which of the two
 * opened it, so its last line names the gesture that works. Escape closes it
 * wherever focus is, since a pointer can open it without focusing anything
 * (WCAG 1.4.13).
 */
function useSparkCard(count: number) {
  const [open, setOpen] = useState(false)
  const [via, setVia] = useState<'pointer' | 'keyboard'>('pointer')
  const [cur, setCur] = useState<number | null>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const timer = useRef<number | undefined>(undefined)
  // Read by the delayed opener, which would otherwise see `open` as it was
  // when the pointer arrived — and relabel a card focus opened meanwhile.
  const isOpen = useRef(false)

  const close = () => {
    window.clearTimeout(timer.current)
    isOpen.current = false
    setOpen(false)
    setCur(null)
  }
  const reveal = (how: 'pointer' | 'keyboard') => {
    // A card already open keeps the words it opened with.
    if (!isOpen.current) setVia(how)
    isOpen.current = true
    setOpen(true)
  }
  const after = (ms: number, fn: () => void) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(fn, ms)
  }

  useEffect(() => () => window.clearTimeout(timer.current), [])
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      window.clearTimeout(timer.current)
      isOpen.current = false
      setOpen(false)
      setCur(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const mouse = (e: PointerEvent) => e.pointerType !== 'touch'
  return {
    open, via, cur, anchor, close,
    /** On the wrapper, which holds the bars AND the card: moving from one to
     *  the other never leaves it, once the close delay has bridged the gap. */
    wrap: {
      onPointerEnter: (e: PointerEvent) => {
        if (mouse(e)) after(SPARK_OPEN_MS, () => reveal('pointer'))
      },
      onPointerLeave: (e: PointerEvent) => { if (mouse(e)) after(SPARK_CLOSE_MS, close) },
    },
    /** On the bars only. Once the pointer is on the card it keeps the last
     *  hour it read, so moving down to read it does not scrub the readout. */
    onBarsMove: (e: PointerEvent<HTMLElement>) => {
      if (!mouse(e) || count <= 0) return
      const r = e.currentTarget.getBoundingClientRect()
      const pitch = (r.width + SPARK_GAP) / count
      const i = Math.min(count - 1, Math.max(0, Math.floor((e.clientX - r.left) / pitch)))
      setCur((c) => (c === i ? c : i))
    },
    row: {
      onFocus: (e: FocusEvent<HTMLElement>) => {
        let visible = false
        try { visible = e.currentTarget.matches(':focus-visible') } catch { /* old engine */ }
        if (visible) reveal('keyboard')
      },
      onBlur: close,
    },
  }
}

/**
 * The row's sparkline, and the card that says what it is.
 *
 * The card is inside the row's button, so it is phrasing content only — no
 * control of its own. That is also why its last line tells the reader to
 * click, or press Enter: either one on the card is that gesture on the row.
 * Its notch sits under the hour being read and slides with the pointer, so
 * the card points at what it is describing rather than at the chart in
 * general.
 *
 * The whole wrapper is hidden from assistive tech, as the sparkline always
 * was. What the card says reaches a screen reader through the row's static
 * description (`sparkDescription`) instead, which is there before the card
 * ever opens.
 */
function TileSpark({ row, data, card }: {
  row: AlertClusterRow
  data: ReturnType<typeof bars>
  card: ReturnType<typeof useSparkCard>
}) {
  const peak = Math.max(1, ...data.map((b) => b.total))
  const band = bandSpan(data)
  const spark = { '--nw-act-n': data.length } as CSSProperties
  const bandCols = band
    ? { gridColumn: `${band.start + 1} / ${band.end + 2}` }
    : undefined
  const reading = card.open && card.cur !== null
  const cardRef = useRef<HTMLSpanElement>(null)
  const [above, setAbove] = useState(false)
  // Placed once per opening, from the card's REAL height, before it paints:
  // under the row, unless the viewport has no room there and more above.
  useLayoutEffect(() => {
    if (!card.open) return
    const a = card.anchor.current?.getBoundingClientRect()
    const h = cardRef.current?.offsetHeight ?? 0
    if (!a || !h) return
    const below = window.innerHeight - a.bottom
    setAbove(below < h + 16 && a.top > below)
  }, [card.open, card.anchor])
  const r = sparkReadout(data, card.cur, row)
  const nub = {
    left: `${(((card.cur ?? (data.length - 1) / 2) + 0.5) / data.length) * 100}%`,
  } as CSSProperties
  return (
    <span
      className="nw-act-sparkw"
      ref={card.anchor}
      aria-hidden="true"
      {...card.wrap}
    >
      <span
        className={`nw-act-spark${reading ? ' is-reading' : ''}`}
        style={spark}
        onPointerMove={card.onBarsMove}
      >
        {band && (
          <span className={`nw-act-spark-l a-${band.anchor}`} style={bandCols}>
            <i>{sparkLabel(row)}</i>
          </span>
        )}
        {band && <span className="nw-act-spark-w" style={bandCols} />}
        {/* Placed EXPLICITLY, like the card's bars and for the same reason:
            a grid auto-places the rest AROUND an explicit item, which slid
            every bar right of the highlight one column over. */}
        {data.map((b, i) => (
          <span
            className={[
              'nw-act-bar',
              b.inCluster ? 'is-in' : '',
              reading && card.cur === i ? 'is-cur' : '',
            ].filter(Boolean).join(' ')}
            key={b.at}
            style={{
              gridColumn: i + 1,
              height: `${Math.max(SPARK_FLOOR_PCT, (b.total / peak) * 100)}%`,
            }}
          />
        ))}
      </span>

      {card.open && (
        <>
          <span className={`nw-act-card${above ? ' is-above' : ''}`} ref={cardRef}>
            <span className="nw-act-card-t">{SPARK_TITLE}</span>
            {r && (
              /* The live part, in a well of its own: it is the one thing on
                 the card that changes while the reader looks at it. */
              <span className="nw-act-card-r">
                <span className="w">{r.when}</span>
                <span className="n">
                  {r.count}
                  {r.inWindow && <em>{BAND_CAP_LEAD}</em>}
                </span>
              </span>
            )}
            <span className="nw-act-card-p">{SPARK_HOW}</span>
            {band ? (
              <span className="nw-act-card-k">
                <span className="kr is-in">
                  <i />
                  <span className="kl">{BAND_CAP_LEAD}</span>
                  <span className="kv">{sparkWindow(row)}</span>
                  <span className="kn">{sparkToneNote(row)}</span>
                </span>
                <span className="kr">
                  <i />
                  <span className="kl">{SPARK_REST}</span>
                  <span className="kn">{SPARK_REST_NOTE}</span>
                </span>
              </span>
            ) : (
              <span className="nw-act-card-p">{SPARK_NO_BAND}</span>
            )}
            <span className="nw-act-card-f">{sparkScale(data)}</span>
            <span className="nw-act-card-go">{SPARK_CTA[card.via]}<ChevIcon /></span>
          </span>
          <span className={`nw-act-card-nub${above ? ' is-above' : ''}`} style={nub} />
        </>
      )}
    </span>
  )
}

/**
 * The Outlook page's row, in the 2026-09-14 design.
 *
 * It is what the reader sees before they see anything else on this page, and
 * the only row on it that opens into a detail view. It used to be a title and
 * a prose subtitle: "Alert activity" over "52 alerts across 16 hosts between
 * 00:28-00:50 EDT. 1 alert still active." — a neutral heading over a sentence,
 * in a slot that is scanned rather than read.
 *
 * Now the three counts carry the row, middot-separated so each lands in one
 * fixation, with the when and the progress on a second line and the shape of
 * the day on the right. The sparkline is the part that earns its space: it is
 * the only thing on the Outlook page that says whether this storm was unusual
 * for this client, which is the question the card behind it exists to answer.
 *
 * ONE TONE per row, from `tileTone`. The chip and the sparkline's highlight
 * wear it together, so the row states its urgency once rather than twice in
 * two colours — except while live, when the chip names the state in the
 * accent ("Investigating") and only the highlight states the condition.
 *
 * Still one thing tall, by the 2026-09-09 ruling for this slot — the full
 * card is what opens on click. Rendering the detail inline would push the
 * 7-day strip below the fold on every page load, for a card that is usually
 * about something already over.
 */
export function AlertClusterTile({ cluster: row, fleetHosts, onOpen }: {
  cluster: AlertClusterRow
  /** Only used to phrase blast radius as a fleet fraction at the `fleet`
   *  rung, exactly as the card does. */
  fleetHosts?: number | null
  onOpen: () => void
}) {
  const chip = tileChip(row)
  const sub = tileSub(row)
  const vol = volumeShort(row)
  const live = row.state === 'live'
  const data = bars(row)
  const card = useSparkCard(data.length)
  const descId = useId()
  return (
    <button
      type="button"
      className={`nw-act t-${tileTone(row)}${live ? ' is-live' : ''}`}
      aria-describedby={data.length > 0 ? descId : undefined}
      onClick={() => { card.close(); onOpen() }}
      {...card.row}
    >
      <span className="nw-act-gl" aria-hidden="true"><SparkMark /></span>
      <span className="nw-act-b">
        <span className="nw-act-l">
          {SLOT_LABEL}
          <span className={`nw-act-chip c-${chip.tone}`}>{chip.text}</span>
        </span>
        <span className="nw-act-h">
          {tileHeadline(row, fleetHosts).map((p, i) => (
            p.strong ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>
          ))}
        </span>
        {vol && (
          <span className={`nw-act-s nw-act-v v-${vol.tone}`}>
            <span className="m" aria-hidden="true" />
            {vol.text}
          </span>
        )}
        {sub.note ? (
          /* Live: the start and what is provisional about it, then the
             progress on a line of its own, since both of its numbers are
             still moving. */
          <>
            <span className="nw-act-s">
              <span className="w">{sub.when}</span>
              <span className="dot" aria-hidden="true">·</span>
              <span className="n">{sub.note}</span>
            </span>
            <span className="nw-act-s"><span className="p">{sub.progress}</span></span>
          </>
        ) : (
          <span className="nw-act-s">
            <span className="w">{sub.when}</span>
            <span className="sep" aria-hidden="true" />
            <span className="p">{sub.progress}</span>
          </span>
        )}
      </span>

      {/* The day's shape, with the cluster's own hours picked out. The bars
          are hidden from assistive tech: the counts beside them already say
          everything they say, and the one thing they add — how unusual these
          hours were — is a comparison a screen reader cannot be handed as a
          number. What the chart IS reaches it through the card's text. */}
      {data.length > 0 && (
        <>
          <TileSpark row={row} data={data} card={card} />
          <span id={descId} hidden>{sparkDescription(row, data)}</span>
        </>
      )}

      {/* A bare chevron, which the previous pass argued against — a grey arrow
          read as decoration on a row with no other affordance. It is not grey
          and the row is no longer bare: it sits in the accent behind a rule of
          its own, at the end of a row whose every other element is a reading.
          The word it replaces cost the sparkline its width. */}
      <span className="nw-act-div" aria-hidden="true" />
      <span className="nw-act-go" aria-hidden="true"><ChevIcon /></span>
    </button>
  )
}

export type ClusterVerdict = 'yes' | 'partly' | 'no'

export interface ClusterFeedbackState {
  verdict: ClusterVerdict
  note?: string | null
  message?: string
}

const VERDICT: Array<{ key: ClusterVerdict; label: string; icon: ReactNode }> = [
  { key: 'yes', label: 'Yes', icon: <ThumbUpIcon /> },
  { key: 'partly', label: 'Partly', icon: <PartlyIcon /> },
  { key: 'no', label: 'No', icon: <ThumbDownIcon /> },
]

/**
 * The feedback row.
 *
 * "Partly" exists because a cluster is several claims at once — this was one
 * event, these hosts are related, that change may be why — and a reader who
 * accepts the grouping but rejects the cause cannot say so with two buttons.
 *
 * What the row PROMISES changed on 2026-09-14, in the design: the note now
 * says the answers help future correlations, where ruling 14 had it say only
 * that they reach the Netwatch team. The second sentence still refuses the
 * thing a reader would otherwise assume — that answering re-runs the
 * analysis in front of them. See FEEDBACK_NOTE in clusterCard.ts.
 */
export function ClusterFeedback({ state, onAnswer }: {
  state?: ClusterFeedbackState | null
  onAnswer: (verdict: ClusterVerdict, note: string) => void
}) {
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')
  const answered = state?.verdict

  return (
    <div className="nw-acf">
      <div className="nw-acf-q">
        <span className="nw-acf-t">{FEEDBACK_TITLE}</span>
        <span className="nw-acf-s">{FEEDBACK_NOTE}</span>
      </div>
      <div className="nw-acf-btns">
        {VERDICT.map((v) => (
          <button
            type="button"
            key={v.key}
            className={`nw-acf-b v-${v.key}${answered === v.key ? ' on' : ''}`}
            aria-pressed={answered === v.key}
            onClick={() => onAnswer(v.key, note)}
          >
            <span className="nw-acf-i" aria-hidden="true">{v.icon}</span>
            {v.label}
          </button>
        ))}
        <button
          type="button"
          className="nw-acf-b nw-acf-note"
          aria-expanded={noteOpen}
          onClick={() => setNoteOpen((o) => !o)}
        >
          <span className="nw-acf-i" aria-hidden="true"><CommentIcon /></span>
          {state?.note ? 'Edit context' : 'Add context'}
        </button>
      </div>
      {noteOpen && (
        <div className="nw-acf-box">
          <textarea
            className="nw-acf-in"
            rows={3}
            maxLength={2000}
            defaultValue={state?.note ?? ''}
            placeholder="What did we get wrong, or what would have helped?"
            onChange={(e) => setNote(e.target.value)}
            aria-label="Note for the Netwatch team"
          />
          <button
            type="button"
            className="nw-acf-send"
            // A note with no verdict still has to go somewhere, so it rides
            // the answer already given, or "partly" — the reading that
            // claims least about what the note says.
            onClick={() => { onAnswer(answered ?? 'partly', note); setNoteOpen(false) }}
          >
            Send to the team
          </button>
        </div>
      )}
      {state?.message && <p className="nw-acf-ack">{state.message}</p>}
    </div>
  )
}

export interface AlertClusterCardProps {
  cluster: AlertClusterRow
  /** Monitored hosts in the whole environment — only used to express blast
   *  radius as a fraction at the `fleet` rung (ruling 19). */
  fleetHosts?: number | null
  /** Render the card's own title. False when the host already names it —
   *  inside the Outlook dialog, whose header says "Alert activity", the
   *  card's own h2 printed the same words twice, one line apart. */
  showTitle?: boolean
  /** Rendered under the card; the feedback row arrives in its own PR. */
  footer?: ReactNode
  /**
   * Open this cluster's alerts on the host's own incidents surface, given
   * the ONSET window (epoch seconds, inclusive both ends — the alert at
   * `to` is one of the cluster's).
   *
   * Optional on purpose. The dashboard has an incidents page and wires it;
   * the embedded widget has nowhere to navigate TO, and falls back to
   * expanding the member list in place — which is what both hosts did
   * before. A card that assumed the page existed would strand the widget on
   * a dead control.
   */
  onViewEvents?: (window: {
    from: number; to: number
    /** The ENVIRONMENT's zone, carried so the page this opens prints the
     *  same clock the card just showed. */
    tz?: string | null
  }) => void
}

/**
 * The finding, with its numbers set apart.
 *
 * Same parts `subtitleFor` assembles, so nothing here is a second source of
 * truth — but the counts carry full ink and weight while the words between
 * them recede, because the sentence's job is to be read at a glance and the
 * numbers are what a reader is glancing for. Emphasis by weight, never by
 * hue: colour on this line states severity.
 */
function Finding({ row, fleetHosts }: {
  row: AlertClusterRow; fleetHosts?: number | null
}) {
  const span = spanWords(row.last_alert_at - row.first_alert_at)
  return (
    <>
      <b>{row.alerts_total}</b>{' '}
      {row.alerts_total === 1 ? 'alert' : 'alerts'}{' '}
      {reachParts(row, fleetHosts).map((p, i) => (
        p.strong ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>
      ))}
      {/* How long the onsets ran — a duration, which the meta line's clock
          range does not state. A live cluster gets nothing here: its window
          is still open, so there is no span to give (printing one would date
          an end that has not happened, ruling 11), and the meta line directly
          above already reads "Still arriving · since 03:00 EDT". */}
      {row.state !== 'live' && span && <> in <b>{span}</b></>}
    </>
  )
}

/**
 * The card's primary action, and its only filled control.
 *
 * It carries its own hint rather than an (i) beside it: the whole button is
 * the thing being explained, and a glyph next to a filled control reads as a
 * second control. Hover, focus and a long-press-equivalent tap all reach it,
 * for the same reason `Hint` accepts all three.
 */
function ViewEvents({ row, onOpen }: { row: AlertClusterRow; onOpen: () => void }) {
  const [hint, setHint] = useState(false)
  const id = useId()
  const label = viewOpenLabel(row)
  return (
    <span
      className="nw-ac-gowrap"
      onMouseEnter={() => setHint(true)}
      onMouseLeave={() => setHint(false)}
    >
      {label ? (
        <button
          type="button"
          className="nw-ac-go"
          aria-describedby={hint ? id : undefined}
          onFocus={() => setHint(true)}
          onBlur={() => setHint(false)}
          onClick={onOpen}
        >
          {label}
          <ChevIcon />
        </button>
      ) : (
        /* Not a disabled button, and not a second verdict. There is nothing
           behind this control when every alert has recovered — the page it
           would open lands on the Live tab and would be empty — so the slot
           accounts for the missing button and says nothing about the cluster.
           The verdict is `activeLine`'s job, two lines below, and an outlined
           green tick here was that same sentence twice. */
        <span className="nw-ac-none" aria-describedby={hint ? id : undefined}>
          {NO_OPEN_NOTE}
        </span>
      )}
      {hint && (
        <span className="nw-ac-hint-p a-end is-go" role="tooltip" id={id}>
          {viewEventsHint(row)}
        </span>
      )}
    </span>
  )
}

export function AlertClusterCard({
  cluster: row, fleetHosts, footer, showTitle = true, onViewEvents,
}: AlertClusterCardProps) {
  const [showEvents, setShowEvents] = useState(false)
  const b = blocks(row)
  const open = openClause(row)
  const found = facts(row, fleetHosts)
  const checks = gaps(row)
  const volume = volumeLine(row)
  // The card's ONE heading. When the host already prints CARD_TITLE above the
  // card (the Outlook dialog does), the finding takes the h2 rather than
  // sitting under a second title saying the same words one line apart.
  const finding = <Finding row={row} fleetHosts={fleetHosts} />
  return (
    <section className="nw-ac" aria-label={CARD_TITLE}>
      <div className="nw-ac-top">
        <div className="nw-ac-topb">
          <div className="nw-ac-meta">
            <span className={`nw-ac-state s-${row.state}`}>{stateChip(row)}</span>
            <span className="nw-ac-dot" aria-hidden="true">·</span>
            {/* The window, meaning the onsets and nothing else (ruling 11).
                Its zone is named here, once, and the chart's axis inherits it
                silently rather than printing a second clock. */}
            <span>
              {row.state === 'live'
                ? `since ${clockAt(row.first_alert_at, row.tz)}`
                : formatWindow(row.first_alert_at, row.last_alert_at, row.tz)}
            </span>
          </div>

          {showTitle ? (
            <>
              <h2 className="nw-ac-title">{CARD_TITLE}</h2>
              <p className="nw-ac-find">{finding}</p>
            </>
          ) : (
            <h2 className="nw-ac-find">{finding}</h2>
          )}

          {/* The clause that decides whether anyone has to act, directly under
              the finding rather than seven blocks below it. Green only when
              the number really is zero: a green tick over "still open on 3
              hosts" would be the card congratulating itself on an outage
              still in progress. */}
          <p className={`nw-ac-open t-${open ? 'amber' : 'green'}`}>
            <span className="nw-ac-open-i" aria-hidden="true">
              {open ? <StillOpenIcon /> : <AllClearIcon />}
            </span>
            {open && <span className="nw-ac-open-n">{open.count}</span>}
            <span className="nw-ac-open-t">{open ? open.text : activeLine(row)}</span>
          </p>

          {/* Whether this volume is normal for the client. Beside the finding
              because it is a reading OF the finding; amber only when it is
              unusual, and never green — "usual" is not an all-clear. */}
          {volume && (
            <p className={`nw-ac-vol t-${volume.tone}`}>
              <b>{volume.lead}.</b> {volume.text}
            </p>
          )}
        </div>

        {onViewEvents ? (
          <ViewEvents
            row={row}
            onOpen={() => onViewEvents({
              from: row.first_alert_at, to: row.last_alert_at, tz: row.tz,
            })}
          />
        ) : (
          <button
            type="button"
            className="nw-ac-go"
            aria-expanded={showEvents}
            onClick={() => setShowEvents((v) => !v)}
          >
            {showEvents ? 'Hide alerts' : viewEventsLabel(row)}
            <ChevIcon />
          </button>
        )}
      </div>

      {row.truncated && <p className="nw-ac-warn">{TRUNCATED_NOTE}</p>}

      <Histogram row={row} />

      {found.length > 0 && (
        <>
          <Facts row={row} rows={found} />
          {b.cause && (
            <p className="nw-ac-hedge">
              A change before a storm is a coincidence in time. Nothing here
              has been shown to have caused anything else.
            </p>
          )}
          {b.fleetNote && (
            <p className="nw-ac-hedge">{fleetNote(row, fleetHosts)}</p>
          )}
        </>
      )}

      {checks.length > 0 && <Checks rows={checks} />}

      {showEvents && <Members row={row} />}

      {footer}
    </section>
  )
}
